import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { FiBackendService } from '../fi-backend/fi-backend.service';
import { FeedbackDialogComponent } from './feedback-dialog.component';
import { IFeedbackContactSupport, IFeedbackDialogData, IFeedbackDialogResult } from './feedback';
import { HtmlPreviewService } from '../fi-print-download/html-preview.service';
import { AppTextService } from '../app-text';
import { FiDialogsService } from '../fi-dialogs';
import { LoadingService } from '../fi-loading/loading.service';
import { FiSnackBarService } from '../fi-snackbar/snack-bar.service';
import { BehaviorSubject, Observable } from 'rxjs';
import {
    ContactSupportDialogSectionType,
    ContactSupportOptionType,
    FiContactSupportDialogComponent,
} from './fi-contact-support-dialog/fi-contact-support-dialog.component';
import { DetectDevicesService } from '../fi-shared-services';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';

@Injectable()
export class FiSupportService {
    private currentContactSupportSection$: BehaviorSubject<ContactSupportDialogSectionType> =
        new BehaviorSubject<ContactSupportDialogSectionType>(ContactSupportDialogSectionType.Main);
    private currentContactSupportOptionType$: BehaviorSubject<ContactSupportOptionType> =
        new BehaviorSubject<ContactSupportOptionType>(undefined);
    private renderer: Renderer2;

    constructor(
        private fiBackend: FiBackendService,
        private customerDataService: FiCustomerDataService,
        private userDataService: FiUserDataService,
        private htmlPreviewService: HtmlPreviewService,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private loadingService: LoadingService,
        private snackBar: FiSnackBarService,
        private detectDevicesService: DetectDevicesService,
        rendererFactory: RendererFactory2,
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    setCurrentSupportSection(newSectionType: ContactSupportDialogSectionType) {
        this.currentContactSupportSection$.next(newSectionType);
    }

    get currentSupportSectionType$(): Observable<ContactSupportDialogSectionType> {
        return this.currentContactSupportSection$.asObservable();
    }

    setCurrentSupportOptionType(newOptionType: ContactSupportOptionType) {
        this.currentContactSupportOptionType$.next(newOptionType);
    }

    get currentSupportOptionType$(): Observable<ContactSupportOptionType> {
        return this.currentContactSupportOptionType$.asObservable();
    }
    getCurrentSupportOptionType() {
        return this.currentContactSupportOptionType$.value;
    }

    isCurrentUserSupportManager() {
        const currentUser = this.userDataService.getUserData();
        if (!currentUser) return false;
        return currentUser.UserId === this.customerDataService.getCustomerData().SupportContact.SupportManagerId;
    }

    openFeedbackDialog(isFeedback: boolean) {
        this.fiDialogsService
            .custom({
                headerText: this.appTextService.get(isFeedback ? 'feedback.send_feedback' : 'feedback.contact_support'),
                yesButtonText: this.appTextService.get('common.send'),
                contentComponentRef: FeedbackDialogComponent,
                contentComponentData: <IFeedbackDialogData>{
                    isFeedback: isFeedback,
                },
                config: {
                    maxWidth: '640px',
                    minWidth: '300px',
                },
            })
            .onYesButtonClick((result) => {
                this.sendFeedback(result);
            });
    }

    public sendFeedback(args: IFeedbackDialogResult) {
        const promisePull: Promise<void>[] = [];
        let attachment: Blob;

        if (args.attachScreenshot) {
            promisePull.push(
                new Promise<void>((resolve, reject) => {
                    setTimeout(() => {
                        const listWrapper = document.getElementsByClassName('CcmListWrapper');
                        if (listWrapper.length) {
                            this.renderer.setStyle(listWrapper[0], 'overflow', 'hidden');
                        }
                        window
                            .html2canvas(document.getElementById('content'), {
                                useCORS: true,
                                background: '#FFFFFF',
                                imageTimeout: 500,
                                logging: false,
                                ignoreElements: (element: HTMLElement) => {
                                    return (
                                        element &&
                                        (element.classList.contains('fi-emoji-mart-overlay') ||
                                            element.tagName === 'img')
                                    );
                                },
                            })
                            .then((canvas) => {
                                attachment = this.htmlPreviewService.dataURItoBlob(
                                    canvas.toDataURL('image/png'),
                                    'image/png',
                                );
                                if (listWrapper.length) {
                                    this.renderer.setStyle(listWrapper[0], 'overflow', 'auto');
                                }
                                resolve();
                            })
                            .catch((e) => {
                                reject();
                                console.error(e);
                            });
                    }, 100);
                }),
            );
        }

        const promise = new Promise<void>((resolve, reject) => {
            Promise.all(promisePull)
                .then(() => {
                    this.submitForm(args.contactSupport, args.files, attachment).then(resolve, reject);
                })
                .catch(reject);
        });

        promise.then(() => {
            this.snackBar.showSnackbar({
                textMessage: this.appTextService.get('feedback.message_sent'),
                buttonText: this.appTextService.get('common.dismiss'),
            });
        });

        return promise;
    }

    private submitForm(feedbackForm: IFeedbackContactSupport, files: File[], attachment: Blob) {
        const fd = new FormData();
        if (attachment) fd.append('attachment', attachment, 'Screenshot.png');

        files.forEach((file) => {
            fd.append(`file`, file);
        });

        fd.append('feedbackForm', JSON.stringify(feedbackForm));

        return this.fiBackend.fetchDataPost('Core/Support/ContactSupport', fd);
    }

    showSupportContactMissingDialog() {
        this.fiDialogsService.notify({
            headerText: this.appTextService.get('support.missing_contact_dialog_header'),
            messageText: this.appTextService.get('support.missing_contact_dialog_message'),
        });
    }

    private showContactSupportDialog() {
        this.fiDialogsService.custom({
            contentComponentRef: FiContactSupportDialogComponent,
            hideFooter: true,
            hideHeader: true,
            config: {
                maxWidth: this.detectDevicesService.isMobilePhone() ? '90%' : '50%',
                width: '100%',
                height: this.detectDevicesService.isMobilePhone() ? '100%' : '710px',
                panelClass: 'fi-full-height-dialog-content',
            },
        });
    }

    checkOpenContactSupport() {
        // todo remove HelpMenuComponent, FeedbackDialogComponent, openFeedbackDialog...

        if (this.customerDataService.getSupportEmailAddress()) {
            this.showContactSupportDialog();
        } else {
            this.showSupportContactMissingDialog();
        }
    }
}
